/**
 * What differs between one park and the next, in one place.
 *
 * Nothing here names De Vergarde. A park's name, its API host and the park id
 * it is scoped to are deployment facts, not source, and the moment one of them
 * is typed into a component the product has quietly become single-tenant. The
 * defaults below are the neutral ones — an empty site name renders the
 * translated generic heading rather than someone else's brand.
 *
 * The values arrive by two routes and both matter:
 *
 *   - `import.meta.env.VITE_*` at build time, for the standalone app;
 *   - the options passed to `mountSearchWidget`, for the WordPress plugin,
 *     which knows the park at page-render time and cannot rebuild a bundle to
 *     say so.
 *
 * The second route wins where both are present, because the plugin is the one
 * that knows which page it is on.
 */

export interface SiteConfig {
  /** Where the public routes live. Same-origin by default. */
  apiBase: string;
  /** Sent as `x-park`. Empty means "whatever the API defaults to". */
  parkId: string;
  /** Shown in the app's own header. Empty falls back to a translated heading. */
  siteName: string;
  /** ISO 4217, for `Intl.NumberFormat`. */
  currency: string;
  /**
   * Where a widget sends a guest to finish an enquiry.
   *
   * The widgets live on WordPress pages and this app owns the enquiry, so a
   * widget's job ends at a deep link. Empty means "we are already in the app",
   * and the widget then calls its `onSelect` prop instead of navigating.
   */
  appUrl: string;
  /**
   * The park's IANA zone. Empty means UTC.
   *
   * Used for one thing: what "today" is, which decides the earliest arrival the
   * calendar will offer. A guest in Amsterdam at 00:30 local is on a new day and
   * UTC still calls it yesterday, so without this the calendar offers a date that
   * has already gone — and west of Greenwich it refuses one that has not.
   */
  timeZone: string;
}

function env(key: string): string {
  // `import.meta.env` is replaced at build time by Vite and is a plain object
  // under vitest, so this reads correctly in both without a guard on `process`.
  const bag = import.meta.env as unknown as Record<string, string | undefined>;
  return bag[key] ?? '';
}

export const defaultConfig: SiteConfig = {
  apiBase: env('VITE_API_BASE') || '/api/public',
  parkId: env('VITE_PARK_ID'),
  siteName: env('VITE_SITE_NAME'),
  currency: env('VITE_CURRENCY') || 'EUR',
  appUrl: env('VITE_APP_URL'),
  timeZone: env('VITE_TIME_ZONE'),
};

export function resolveConfig(overrides: Partial<SiteConfig> = {}): SiteConfig {
  const out = { ...defaultConfig };
  for (const [k, v] of Object.entries(overrides)) {
    if (v !== undefined && v !== null && v !== '') {
      (out as unknown as Record<string, string>)[k] = String(v);
    }
  }
  return out;
}
